<template>
  <v-navigation-drawer
    :value="value"
    @input="$emit('input', $event)"
    app
    right
    temporary
    width="420"
    class="cart-drawer"
    :class="{ mobile: $vuetify.breakpoint.smAndDown }">
    <div class="cart-header">
      <div class="d-flex align-center">
        <v-icon left color="white">mdi-cart</v-icon>
        <span class="title-text">Warenkorb</span>
        <v-chip v-if="totalItems > 0" small color="white" text-color="primary" class="ml-2">{{ totalItems }}</v-chip>
      </div>
      <v-btn icon @click="$emit('input', false)" color="white">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <div v-if="items.length === 0" class="empty-cart">
      <v-icon size="80" color="grey darken-1">mdi-cart-outline</v-icon>
      <div class="empty-text">Dein Warenkorb ist leer</div>
      <div class="caption grey--text">Füge Produkte aus dem Shop hinzu</div>
      <v-btn color="primary" depressed rounded class="mt-6" @click="$emit('input', false)">
        <v-icon left>mdi-storefront</v-icon>
        Zum Shop
      </v-btn>
    </div>

    <div v-else class="cart-body">
      <div class="cart-items">
        <div v-for="item in items" :key="getCartItemKey(item)" class="cart-item" :class="{ 'is-digital': item.productType === 'digital' }">
          <div class="item-info">
            <div class="item-title">
              <v-icon v-if="item.productType === 'digital'" small color="primary" class="mr-1">mdi-download</v-icon>
              {{ item.title }}
            </div>
            <div v-if="hasOptions(item)" class="item-options mt-1">
              <v-chip x-small outlined color="grey lighten-2" class="mr-1 mb-1" v-for="(val, name) in item.selectedOptions" :key="name">
                {{ name }}: {{ val }}
              </v-chip>
            </div>
            <div class="caption grey--text">
              <span v-if="item.productType === 'digital'">Download</span>
              <span v-else>{{ item.minQuantity }} – {{ item.maxQuantity }} Stk</span>
            </div>
          </div>

          <div class="item-actions">
            <div v-if="item.productType !== 'digital'" class="qty-stepper">
              <v-btn color="white" icon x-small @click="changeQty(item, -1)" :disabled="item.quantity <= item.minQuantity">
                <v-icon small>mdi-minus</v-icon>
              </v-btn>
              <input
                :value="item.quantity"
                type="number"
                class="qty-input"
                :min="item.minQuantity"
                :max="item.maxQuantity"
                @input="setQty(item, $event.target.value)"
                @blur="clampQty(item)" />
              <v-btn color="white" icon x-small @click="changeQty(item, 1)" :disabled="item.quantity >= item.maxQuantity">
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
            </div>

            <v-btn icon small color="error" @click="$emit('remove-item', getCartItemKey(item))">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <div class="cart-form">
        <v-select
          v-model="selectedStudio"
          :items="studioOptions"
          item-text="name"
          item-value="_id"
          label="Studio *"
          outlined
          dense
          dark
          hide-details
          :rules="[(v) => !!v || 'Pflichtfeld']"
          class="mb-3" />

        <v-textarea
          v-model="note"
          label="Notiz (optional)"
          placeholder="Anmerkungen zur Bestellung..."
          outlined
          dense
          dark
          rows="3"
          hide-details
          auto-grow />
      </div>

      <div class="cart-footer">
        <div class="cart-summary">
          <span>Total Artikel:</span>
          <strong>{{ totalItems }}</strong>
        </div>
        <div v-if="hasDigitalItems" class="caption mb-2" style="color: #4ade80">
          <v-icon small color="success">mdi-information-outline</v-icon>
          Downloads werden direkt nach dem Absenden bereitgestellt.
        </div>
        <v-btn block large color="primary" depressed rounded class="mb-2" :loading="submitting" :disabled="!canSubmit" @click="submit">
          <v-icon left>mdi-check-circle</v-icon>
          Bestellung absenden
        </v-btn>
        <v-btn block text small color="error" @click="confirmClear" :disabled="submitting">Warenkorb leeren</v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "CartDrawer",
  props: {
    value: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
    studioOptions: { type: Array, default: () => [] },
    defaultStudioId: { type: String, default: null },
    submitting: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedStudio: this.defaultStudioId,
      note: "",
    }
  },
  computed: {
    totalItems() {
      return this.items.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0)
    },
    canSubmit() {
      return this.items.length > 0 && this.selectedStudio && !this.submitting
    },
    hasDigitalItems() {
      return this.items.some((i) => i.productType === "digital")
    },
  },
  watch: {
    value(open) {
      if (open && !this.selectedStudio) this.selectedStudio = this.defaultStudioId
    },
    defaultStudioId(val) {
      if (!this.selectedStudio) this.selectedStudio = val
    },
  },
  methods: {
    getCartItemKey(item) {
      if (!item.selectedOptions || Object.keys(item.selectedOptions).length === 0) return item.product
      const optStr = Object.entries(item.selectedOptions).sort().map(([k, v]) => `${k}:${v}`).join(",")
      return `${item.product}_${optStr}`
    },
    hasOptions(item) {
      return item.selectedOptions && Object.keys(item.selectedOptions).length > 0
    },
    changeQty(item, delta) {
      if (item.productType === "digital") return
      const newQty = (item.quantity || 0) + delta
      this.$emit("update-quantity", { itemKey: this.getCartItemKey(item), quantity: newQty })
    },
    setQty(item, val) {
      if (item.productType === "digital") return
      const v = parseInt(val) || item.minQuantity
      this.$emit("update-quantity", { itemKey: this.getCartItemKey(item), quantity: v })
    },
    clampQty(item) {
      if (item.productType === "digital") return
      const v = Math.max(item.minQuantity, Math.min(item.maxQuantity, item.quantity || item.minQuantity))
      this.$emit("update-quantity", { itemKey: this.getCartItemKey(item), quantity: v })
    },
    submit() {
      if (!this.canSubmit) return
      this.$emit("submit-order", { studio: this.selectedStudio, note: this.note })
      this.note = ""
    },
    confirmClear() {
      if (confirm("Warenkorb wirklich leeren?")) this.$emit("clear-cart")
    },
  },
}
</script>

<style scoped>
.cart-drawer {
  display: flex !important;
  flex-direction: column;
  background: #1e1e1e !important;
  color: #fff !important;
}

.cart-drawer.mobile {
  width: 100vw !important;
}

.cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #009640 0%, #006b2d 100%);
  color: #fff;
  flex-shrink: 0;
}

.title-text {
  font-size: 18px;
  font-weight: 600;
  margin-left: 8px;
}

.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-text {
  margin-top: 16px;
  font-size: 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.cart-body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
}

.cart-items {
  flex-grow: 1;
  overflow-y: auto;
  padding: 12px 16px;
}

.cart-item {
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  gap: 10px;
}

.cart-item.is-digital {
  border-color: rgba(0, 150, 64, 0.3);
  background: rgba(0, 150, 64, 0.05);
}

.item-info {
  flex-grow: 1;
  min-width: 0;
}

.item-title {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.qty-stepper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 2px;
}

.qty-input {
  width: 36px;
  text-align: center;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.cart-form {
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.cart-footer {
  padding: 16px;
  background: rgba(255, 255, 255, 0.03);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.cart-summary {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
}
</style>
